import type {
  BulkActionResult, BulkApprovalsBody, BulkRecutResult, BulkRerenderResponse, ReviewLabel,
  ConflictEdge,
  CreateStoreBody, DeletePreview, DeletionStatus, DesignConfig, FeedRunResult, FeedStatus, FilterClause,
  FilterPreviewResponse,
  LifestyleReason, LifestyleState,
  ListRendersParams, ListRendersResponse,
  OverrideRow, PreviewResult, ProductDiagnostic, QaPendingResponse, QaReviewedResponse, QaReviewFilter,
  QaVerdict, QaVerdictResult,
  RerunResult,
  QueueCount, QueueResolution, QueueResponse, QueueRunResult,
  RecordApprovalBody,
  RunEstimate,
  RecordOverridesBody,
  RenderRow,
  CutoutState, JobExecutionStatus,
  ReprocessMethod,
  SampleProduct, SampleRenderResponse,
  SourceFields, Store,
  Me, AdminUser, CreateUserBody, CreateUserResult, UsageRow,
  OrgRole, Organization, OrgSummary, OrgDetail, OrgUser, StoreGroup,
} from './types';

const BASE = '';

export class HttpError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(`HTTP ${status}: ${detail}`);
    this.status = status;
    this.detail = detail;
  }
}

/** Extract the human `detail` string from a FastAPI error body (W-C [UX-4]):
 * raw wire payloads like `{"detail":"config 'x' not found"}` were rendered
 * verbatim on every error surface. Falls back to the raw text / statusText. */
export function parseDetail(raw: string, fallback: string): string {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'detail' in parsed) {
      const d = (parsed as { detail: unknown }).detail;
      if (typeof d === 'string') return d;
      if (d !== undefined) return JSON.stringify(d);
    }
  } catch {
    /* not JSON — use the raw text */
  }
  return raw || fallback;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 401) {
    // Identity probes + login posts handle 401 themselves (show an error / set
    // anon state); everything else means a dead session → bounce to login.
    const isAuthProbe =
      path.endsWith('/api/login') ||
      path.endsWith('/api/auth/login') ||
      path.endsWith('/api/me');
    if (!isAuthProbe) {
      window.location.href = '/login';
    }
    throw new HttpError(401, 'Your session has expired — please sign in again.');
  }
  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    throw new HttpError(res.status, parseDetail(raw, res.statusText));
  }
  if (res.status === 204 || res.status === 202) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.startsWith('image/')) return (await res.blob()) as T;
  return res.json();
}

function storeScope(storeId: string | undefined, suffix: string): string {
  return storeId
    ? `/api/stores/${encodeURIComponent(storeId)}${suffix}`
    : `/api${suffix}`;
}

export const api = {
  // --- legacy operator (shared-password) login ---
  login: (password: string) =>
    request<{ ok: true }>('/api/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => request<{ ok: true }>('/api/logout', { method: 'POST' }),

  // --- identity / email+password auth (multi-tenancy) ---
  me: () => request<Me>('/api/me'),
  authLogin: (email: string, password: string) =>
    request<{ ok: true; user: { id: string; email: string; role: string } }>(
      '/api/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    ),
  setPassword: (token: string, password: string) =>
    request<{ ok: true }>('/api/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  // --- admin account management (admin role only on the backend) ---
  adminListUsers: () => request<AdminUser[]>('/api/admin/users'),
  adminCreateUser: (body: CreateUserBody) =>
    request<CreateUserResult>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminSetUserActive: (userId: string, active: boolean) =>
    request<{ ok: true; disabled: boolean }>(
      `/api/admin/users/${encodeURIComponent(userId)}/active`,
      { method: 'POST', body: JSON.stringify({ active }) },
    ),
  adminSetUserOrg: (userId: string, body: { org_id: string | null; org_role: OrgRole; group_ids: string[] }) =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(userId)}/org`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  adminUsage: () => request<UsageRow[]>('/api/admin/usage'),

  // --- organizations (access levels, 2026-09-02) ---
  listOrgs: () => request<OrgSummary[]>('/api/orgs'),
  createOrg: (body: { id: string; name: string }) =>
    request<Organization>('/api/orgs', { method: 'POST', body: JSON.stringify(body) }),
  renameOrg: (orgId: string, body: { name: string }) =>
    request<{ ok: true }>(`/api/orgs/${encodeURIComponent(orgId)}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOrg: (orgId: string) =>
    request<void>(`/api/orgs/${encodeURIComponent(orgId)}`, { method: 'DELETE' }),
  getOrg: (orgId: string) => request<OrgDetail>(`/api/orgs/${encodeURIComponent(orgId)}`),
  createGroup: (orgId: string, body: { id?: string; name: string }) =>
    request<StoreGroup>(`/api/orgs/${encodeURIComponent(orgId)}/groups`, { method: 'POST', body: JSON.stringify(body) }),
  renameGroup: (orgId: string, gid: string, body: { name: string }) =>
    request<{ ok: true }>(`/api/orgs/${encodeURIComponent(orgId)}/groups/${encodeURIComponent(gid)}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteGroup: (orgId: string, gid: string) =>
    request<void>(`/api/orgs/${encodeURIComponent(orgId)}/groups/${encodeURIComponent(gid)}`, { method: 'DELETE' }),
  assignStoreToOrg: (orgId: string, storeId: string, body: { group_id: string | null }) =>
    request<{ ok: true }>(`/api/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}`, { method: 'PUT', body: JSON.stringify(body) }),
  unassignStore: (orgId: string, storeId: string) =>
    request<void>(`/api/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}`, { method: 'DELETE' }),
  inviteOrgUser: (orgId: string, body: { email: string; display_name?: string; org_role: OrgRole; group_ids: string[]; password?: string }) =>
    request<{ user: OrgUser; set_password_token: string | null }>(`/api/orgs/${encodeURIComponent(orgId)}/users`, { method: 'POST', body: JSON.stringify(body) }),
  updateOrgUser: (orgId: string, uid: string, body: { org_role?: OrgRole; group_ids?: string[] }) =>
    request<{ ok: true }>(`/api/orgs/${encodeURIComponent(orgId)}/users/${encodeURIComponent(uid)}`, { method: 'PUT', body: JSON.stringify(body) }),
  setOrgUserActive: (orgId: string, uid: string, active: boolean) =>
    request<{ ok: true; disabled: boolean }>(`/api/orgs/${encodeURIComponent(orgId)}/users/${encodeURIComponent(uid)}/active`, { method: 'POST', body: JSON.stringify({ active }) }),

  // --- Stores (tier 4a) ---
  listStores: () => request<Store[]>('/api/stores'),
  getStore: (id: string) =>
    request<Store>(`/api/stores/${encodeURIComponent(id)}`),
  updateStore: (id: string, store: Store) =>
    request<{ ok: true }>(`/api/stores/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(store),
    }),
  createStore: (body: CreateStoreBody) =>
    request<Store>('/api/stores', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteStore: (storeId: string) =>
    request<void>(`/api/stores/${encodeURIComponent(storeId)}`, { method: 'DELETE' }),
  deleteStorePreview: (storeId: string) =>
    request<DeletePreview>(`/api/stores/${encodeURIComponent(storeId)}/delete-preview`),
  deletionStatus: (storeId: string) =>
    request<DeletionStatus | null>(`/api/stores/${encodeURIComponent(storeId)}/deletion-status`)
      .catch((e) => {
        if (e instanceof HttpError && e.status === 404) return null;
        throw e;
      }),

  // --- Configs (now store-scoped; pass storeId to use the new namespace) ---
  listConfigs: (storeId?: string) =>
    request<DesignConfig[]>(storeScope(storeId, '/configs')),
  getConfig: (name: string, storeId?: string) =>
    request<DesignConfig>(
      storeScope(storeId, `/configs/${encodeURIComponent(name)}`),
    ),
  // PUT to `asName` (the name the editor LOADED) so a body.name that differs
  // is a rename-as-move server-side (W-C [UX-2]). Defaults to config.name.
  putConfig: (config: DesignConfig, storeId?: string, asName?: string) =>
    request<{ ok: true; renamed_from?: string }>(
      storeScope(storeId, `/configs/${encodeURIComponent(asName ?? config.name)}`),
      { method: 'PUT', body: JSON.stringify(config) },
    ),
  deleteConfig: (name: string, storeId?: string) =>
    request<void>(
      storeScope(storeId, `/configs/${encodeURIComponent(name)}`),
      { method: 'DELETE' },
    ),
  duplicateConfig: (name: string, storeId?: string) =>
    request<DesignConfig>(
      storeScope(storeId, `/configs/${encodeURIComponent(name)}/duplicate`),
      { method: 'POST' },
    ),
  configsConflictGraph: (storeId: string) =>
    request<ConflictEdge[]>(storeScope(storeId, '/configs/conflict-graph')),

  feedFields: () => request<string[]>('/api/feed/fields'),
  storeFields: (storeId: string) =>
    request<SourceFields>(`/api/stores/${encodeURIComponent(storeId)}/fields`),

  filterPreview: (storeId: string, body: { config_name: string; filter: FilterClause | null }) =>
    request<FilterPreviewResponse>(
      `/api/stores/${encodeURIComponent(storeId)}/filter-preview`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  bqSchema: (table: string) =>
    request<Array<{ name: string; type: string; mode: string }>>(
      `/api/bq-schema?table=${encodeURIComponent(table)}`,
    ),
  sampleProducts: (storeId?: string) =>
    request<SampleProduct[]>(storeScope(storeId, '/sample-products')),
  refreshSampleProducts: () =>
    request<{ ok: true }>('/api/sample-products/refresh', { method: 'POST' }),

  uploadAsset: async (
    kind: 'background' | 'logo' | 'lifestyle',
    file: File,
    storeId?: string,
  ): Promise<{ path: string; public_url: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    const url = storeId
      ? `${BASE}/api/stores/${encodeURIComponent(storeId)}/assets?kind=${kind}`
      : `${BASE}/api/assets?kind=${kind}`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) throw new HttpError(res.status, await res.text());
    return res.json();
  },

  uploadCutout: async (
    storeId: string,
    productId: string,
    file: File,
  ): Promise<{ status: string; approved_image_url: string }> => {
    const fd = new FormData();
    fd.append('product_id', productId);
    fd.append('file', file);
    const res = await fetch(
      `${BASE}/api/stores/${encodeURIComponent(storeId)}/cutout-approvals/upload`,
      { method: 'POST', credentials: 'include', body: fd },
    );
    if (!res.ok) {
      throw new HttpError(res.status, parseDetail(await res.text().catch(() => ''), res.statusText));
    }
    return res.json();
  },

  /** Rendered preview plus whether the lifestyle split actually happened.
   *
   *  Uses fetch directly rather than `request` because the answer is in the
   *  response HEADERS, not the body — an unresolved photo returns a perfectly
   *  valid full-canvas JPEG and the editor would otherwise have no way to know
   *  it isn't the split the operator asked for. */
  preview: async (
    productId: string,
    config: DesignConfig,
    storeId?: string,
  ): Promise<PreviewResult> => {
    const res = await fetch(BASE + storeScope(storeId, '/preview'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_id: productId, config }),
    });
    if (res.status === 401) {
      window.location.href = '/login';
      throw new HttpError(401, 'Your session has expired — please sign in again.');
    }
    if (!res.ok) {
      throw new HttpError(
        res.status,
        parseDetail(await res.text().catch(() => ''), res.statusText),
      );
    }
    return {
      blob: await res.blob(),
      state: (res.headers.get('X-Lifestyle-State') as LifestyleState) ?? 'off',
      reason: res.headers.get('X-Lifestyle-Reason') as LifestyleReason | null,
      cutoutState: (res.headers.get('X-Cutout-State') as CutoutState) ?? 'cached',
    };
  },

  sampleRender: (storeId: string, config: DesignConfig) =>
    request<SampleRenderResponse>(
      `/api/stores/${encodeURIComponent(storeId)}/sample-render`,
      { method: 'POST', body: JSON.stringify({ config }) },
    ),

  // --- Cutouts (Wave 12) ---
  listCutoutOverrides: (storeId: string) =>
    request<OverrideRow[]>(storeScope(storeId, '/cutout-overrides')),

  createCutoutOverrides: (storeId: string, body: RecordOverridesBody) =>
    request<{ count: number }>(storeScope(storeId, '/cutout-overrides'), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  dropCutoutOverride: (storeId: string, productId: string) =>
    request<void>(
      storeScope(storeId, `/cutout-overrides/${encodeURIComponent(productId)}`),
      { method: 'DELETE' },
    ),

  approveCutout: (storeId: string, body: RecordApprovalBody) =>
    request<{ status: string }>(storeScope(storeId, '/cutout-approvals'), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- Wave 10: needs-review queue + bulk actions ---
  listNeedsReviewQueue: (
    storeId: string,
    resolution: QueueResolution = 'pending',
  ) =>
    request<QueueResponse>(
      storeScope(storeId, `/needs-review-queue?resolution=${resolution}`),
    ),

  bulkApproveCutouts: (storeId: string, body: BulkApprovalsBody) =>
    request<BulkActionResult>(storeScope(storeId, '/cutout-approvals/bulk'), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  bulkDropOverrides: (storeId: string, productIds: string[]) =>
    request<BulkActionResult>(storeScope(storeId, '/cutout-overrides/bulk-drop'), {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds }),
    }),

  bulkRejectOverrides: (
    storeId: string,
    body: { product_ids: string[]; reason?: string },
  ) =>
    request<BulkActionResult>(storeScope(storeId, '/cutout-overrides/bulk-reject'), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  bulkRerender: (storeId: string, productIds: string[]) =>
    request<BulkRerenderResponse>(storeScope(storeId, '/cutout-overrides/bulk-rerender'), {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds }),
    }),

  bulkEnqueueReprocess: (storeId: string, productIds: string[], method: ReprocessMethod) =>
    request<{ count: number }>(storeScope(storeId, '/cutout-overrides/bulk-rerender'), {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds, force_tier: method }),
    }),

  /** Un-approve + queue a re-cut. Plain bulkEnqueueReprocess is a NO-OP on an
   *  operator-approved product: the runner prefers the pinned cutout over the forced
   *  tier. This revokes the pin first. Products already at `method` come back in
   *  `diverted_ids` — parked for manual instead, since a re-cut can't change them. */
  bulkRecut: (storeId: string, productIds: string[], method: ReprocessMethod) =>
    request<BulkRecutResult>(storeScope(storeId, '/cutout-recut/bulk'), {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds, force_tier: method }),
    }),

  /** Disapprove + hand to a human (manual cutout upload or a 3p API). */
  bulkManualPark: (storeId: string, productIds: string[]) =>
    request<BulkActionResult>(storeScope(storeId, '/cutout-manual-park/bulk'), {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds }),
    }),

  /** Distinct review labels + counts — feeds the filter select and apply combobox. */
  listReviewLabels: (storeId: string) =>
    request<{ labels: ReviewLabel[] }>(storeScope(storeId, '/review-labels')),

  /** Mark products triaged. Idempotent server-side (MERGE). */
  applyReviewLabel: (storeId: string, productIds: string[], label: string) =>
    request<BulkActionResult>(storeScope(storeId, '/review-labels/bulk'), {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds, label }),
    }),

  /** Remove `label` from these products; omit the label to clear all of theirs. */
  removeReviewLabel: (storeId: string, productIds: string[], label?: string) =>
    request<BulkActionResult>(storeScope(storeId, '/review-labels/bulk-remove'), {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds, label: label ?? null }),
    }),

  reprocessQueueCount: (storeId: string) =>
    request<QueueCount>(storeScope(storeId, '/reprocess-queue/count')),

  /** scope 'overrides' (ui-light): --only-overrides execution override —
   *  renders the queued re-cuts without draining the whole catalog. Pass
   *  productIds to scope the run to exactly those queued products (the card's
   *  single-product "Run now", ~2–4 min on one task); omit to drain the whole
   *  queue. 'full' keeps the whole-job behaviour. 409s while an execution is
   *  already running. */
  reprocessQueueRun: (
    storeId: string,
    scope: 'full' | 'overrides' = 'full',
    productIds?: string[],
  ) =>
    request<QueueRunResult>(storeScope(storeId, '/reprocess-queue/run'), {
      method: 'POST',
      body: JSON.stringify(
        productIds ? { scope, product_ids: productIds } : { scope },
      ),
    }),

  jobExecutionStatus: (storeId: string, execution: string) =>
    request<{ status: JobExecutionStatus }>(
      storeScope(storeId, `/job-executions/status?execution=${encodeURIComponent(execution)}`),
    ),

  // W-G [CO-8]: upper-bound cost of the next full Job execution.
  runEstimate: (storeId: string) =>
    request<RunEstimate>(storeScope(storeId, '/run-estimate')),

  rejectOverride: (storeId: string, productId: string, reason?: string) =>
    request<{ status: string }>(
      storeScope(storeId, `/cutout-overrides/${encodeURIComponent(productId)}/reject`),
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  unrejectOverride: (storeId: string, productId: string) =>
    request<void>(
      storeScope(storeId, `/cutout-overrides/${encodeURIComponent(productId)}/reject`),
      { method: 'DELETE' },
    ),

  // --- Feed output (spec 2026-09-03 §13) ---
  regenerateFeed: (storeId: string, force = false) =>
    request<FeedRunResult>(storeScope(storeId, '/feeds/regenerate'), {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),
  feedStatus: (storeId: string, history = 10) =>
    request<FeedStatus>(storeScope(storeId, `/feeds/status?history=${history}`)),

  // --- Wave 12: All-renders gallery + lookup ---
  listRenders: (storeId: string, params: ListRendersParams = {}) => {
    // Local renamed `search` -> `sp`: `params.search` (Task 20) and the
    // pre-existing `qs` (query string) below would otherwise collide with it.
    const sp = new URLSearchParams();
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.offset !== undefined) sp.set('offset', String(params.offset));
    if (params.tier) sp.set('tier', params.tier);
    if (params.max_score !== undefined) sp.set('max_score', String(params.max_score));
    if (params.since) sp.set('since', params.since);
    if (params.sort) sp.set('sort', params.sort);
    if (params.status) sp.set('status', params.status);
    if (params.is_approved !== undefined) sp.set('is_approved', String(params.is_approved));
    if (params.graded !== undefined) sp.set('graded', String(params.graded));
    if (params.search) sp.set('search', params.search);
    const qs = sp.toString();
    return request<ListRendersResponse>(
      storeScope(storeId, `/renders${qs ? '?' + qs : ''}`),
    );
  },

  getRender: (storeId: string, productId: string) =>
    request<RenderRow>(
      storeScope(storeId, `/renders/${encodeURIComponent(productId)}`),
    ),

  getProductDiagnostic: (storeId: string, productId: string) =>
    request<ProductDiagnostic>(
      storeScope(storeId, `/products/${encodeURIComponent(productId)}/diagnostic`),
    ),

  rerunProduct: (storeId: string, productId: string) =>
    request<RerunResult>(
      storeScope(storeId, `/products/${encodeURIComponent(productId)}/rerun`),
      { method: 'POST' },
    ),

  // --- QA review ---
  // storeId (optional): scopes the QA gate to that store's tables. Omitted → the
  // gate's default store (widforss), so existing single-store callers are unchanged.
  getQaPending: (limit = 50, storeId?: string) =>
    request<QaPendingResponse>(
      `/api/qa/pending?limit=${limit}` +
        (storeId ? `&store=${encodeURIComponent(storeId)}` : '')),
  getQaReviewed: (filter: QaReviewFilter = 'disapproved', limit = 60, offset = 0,
                  search?: string,
                  opts?: {
                    tier?: string; max_score?: number; sort?: string;
                    // split-review: auto-balanced reviewer lanes, keyset cursor, lock-in filter
                    lane?: number; lanes?: number; afterId?: string; hideReviewed?: boolean;
                    // hide products with a PENDING override (re-cut in flight) —
                    // their cutout is about to change, so scoring them is wasted
                    hideQueued?: boolean;
                    // value-sort keyset cursor (importance/sales/stock page on a rank)
                    afterRank?: number;
                    // multi-store: scope the review gallery to this store
                    store?: string;
                    // review labels: a UNION of ticked entries — label names and/or
                    // UNLABELLED for "not yet triaged"
                    labels?: string[];
                    // ...minus anything carrying one of these ("tagged X but not Z").
                    // Repeated params, not a signed `labels=-done`: `-done` is a legal
                    // label under LABEL_RE, so a sign prefix would be ambiguous.
                    excludeLabels?: string[];
                  }) =>
    request<QaReviewedResponse>(
      `/api/qa/reviewed?filter=${filter}&limit=${limit}&offset=${offset}` +
        (search ? `&search=${encodeURIComponent(search)}` : '') +
        (opts?.tier ? `&tier=${encodeURIComponent(opts.tier)}` : '') +
        (opts?.max_score != null ? `&max_score=${opts.max_score}` : '') +
        (opts?.sort ? `&sort=${opts.sort}` : '') +
        (opts?.lanes != null ? `&lanes=${opts.lanes}&lane=${opts.lane ?? 0}` : '') +
        (opts?.afterId ? `&after_id=${encodeURIComponent(opts.afterId)}` : '') +
        (opts?.afterRank != null ? `&after_rank=${opts.afterRank}` : '') +
        (opts?.hideReviewed ? '&hide_reviewed=true' : '') +
        (opts?.hideQueued ? '&hide_queued=true' : '') +
        (opts?.labels?.length
          ? opts.labels.map((l) => `&labels=${encodeURIComponent(l)}`).join('')
          : '') +
        (opts?.excludeLabels?.length
          ? opts.excludeLabels.map((l) => `&exclude_labels=${encodeURIComponent(l)}`).join('')
          : '') +
        (opts?.store ? `&store=${encodeURIComponent(opts.store)}` : ''),
    ),
  // remediate=false: a human verdict (paste flow OR gallery override) never
  // auto-triggers snapedit — that stays an explicit, operator-only action.
  submitQaVerdicts: (verdicts: QaVerdict[], storeId?: string) =>
    request<QaVerdictResult>(
      '/api/qa/verdicts?remediate=false' +
        (storeId ? `&store=${encodeURIComponent(storeId)}` : ''), {
      method: 'POST',
      body: JSON.stringify(verdicts),
    }),
};
